import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Globe2, Ship, ShieldCheck, Sprout, Handshake, BarChart3 } from "lucide-react";
import { IMAGES, COMPANY, STATS, PRODUCTS, REGIONS } from "@/lib/site";
import { Section, Overline, FadeIn } from "@/components/Primitives";

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100svh] flex items-end px-4 sm:px-6 lg:px-10 pt-28 pb-10 overflow-hidden bg-[#FDFBF7]"
      >
        <div className="relative w-full h-[80svh] sm:h-[85svh] rounded-[28px] overflow-hidden mx-auto max-w-[1500px]">
          <img
            src={IMAGES.hero}
            alt="Premium wheat field at sunrise"
            className="absolute inset-0 h-full w-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F]/95 via-[#1A1A1A]/65 to-[#1A1A1A]/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F]/70 via-[#1A1A1A]/30 to-transparent" />

          <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-12 lg:p-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-7"
              data-testid="hero-overline"
            >
              <p className="font-serif-display text-[#F1E0CF] text-2xl sm:text-3xl lg:text-[2.2rem] leading-tight tracking-tight font-bold">
                Farmico International Trading FZE
              </p>
              <p className="mt-2 font-serif-display text-[#D8A48F] text-2xl sm:text-3xl lg:text-[2.2rem] leading-tight tracking-tight font-bold">
                Farmico Proteins FZCO
              </p>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-serif-display text-[#FDFBF7] text-5xl sm:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight max-w-5xl drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
              data-testid="hero-title"
            >
              Trading the world's
              <br />
              <em className="text-[#E6C9B5] not-italic">Pulses & Oilseeds.</em>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-8 max-w-2xl text-base sm:text-lg text-[#F6F4F0]/90 leading-relaxed"
            >
              From the UAE, we connect origins to demand across six continents — a global multi-commodity
              trading house and international brokerage for the pulses industry.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#1A1A1A] px-7 py-4 text-sm font-semibold hover:bg-[#D8A48F] transition-colors"
              >
                Explore commodities <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="inline-flex items-center gap-2 rounded-full border border-[#FDFBF7]/40 text-[#FDFBF7] px-7 py-4 text-sm font-semibold hover:bg-[#FDFBF7]/10 transition-colors backdrop-blur-sm"
              >
                Request a quote
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="px-6 sm:px-12 lg:px-24 py-20 bg-[#FDFBF7]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#EAE7E0]">
          {STATS.map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.08} className="px-6 first:pl-0 last:pr-0">
              <p className="font-serif-display text-4xl sm:text-5xl text-[#2A4D3B]">{s.value}</p>
              <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.18em] text-[#4A4540] leading-snug">
                {s.label}
              </p>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* INTRO / TWO ENTITIES */}
      <Section testid="intro-section" className="bg-[#F6F4F0]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <Overline>Who we are</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.08] text-[#1A1A1A]">
              Headquartered in UAE,
              <br />
              built for global reach.
            </h2>
            <p className="mt-7 text-[#4A4540] leading-relaxed">
              Headquartered in UAE, the FARMICO Group — built for global reach, executed with
              origin-level discipline. Two specialised entities operate under one trading
              philosophy.
            </p>
            <Link
              to="/about"
              data-testid="intro-cta-about"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#2A4D3B] hover:text-[#3A634E] link-underline"
            >
              About the Group →
            </Link>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            <FadeIn>
              <article className="h-full rounded-3xl bg-white p-8 sm:p-10 shadow-sm border border-[#EAE7E0] hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <h3 className="font-serif-display text-2xl text-[#1A1A1A]">{COMPANY.fze}</h3>
                <p className="mt-4 text-sm text-[#4A4540] leading-relaxed">
                  Multi-commodity trading desk specialised in physical pulses, oilseeds and other
                  cash crops — with execution capabilities across containerised and break-bulk
                  shipments.
                </p>
                <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#2A4D3B]">
                  <Sprout className="h-4 w-4" /> Physical trading
                </div>
              </article>
            </FadeIn>
            <FadeIn delay={0.1}>
              <article className="h-full rounded-3xl bg-[#2A4D3B] text-[#F6F4F0] p-8 sm:p-10 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <h3 className="font-serif-display text-2xl text-[#FDFBF7]">{COMPANY.fzco}</h3>
                <p className="mt-4 text-sm text-[#C8C2B5] leading-relaxed">
                  Specialised brokerage for the global pulses industry. We connect origin shippers to
                  destination buyers with deep market intelligence and trusted execution.
                </p>
                <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#D8A48F]">
                  <Handshake className="h-4 w-4" /> Pulses brokerage
                </div>
              </article>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* PRODUCTS BENTO */}
      <Section testid="products-bento" className="bg-[#FDFBF7]">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <Overline>Commodities</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl tracking-tight leading-tight text-[#1A1A1A]">
              A complete portfolio across origins & grades.
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="products-cta"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2A4D3B] link-underline self-start"
          >
            View all products <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {PRODUCTS.map((p, i) => (
            <FadeIn key={p.slug} delay={i * 0.1}>
              <Link
                to="/products"
                data-testid={`product-card-${p.slug}`}
                className="group block relative h-full rounded-3xl overflow-hidden bg-white border border-[#EAE7E0] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 lg:min-h-[420px]"
              >
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-serif-display text-2xl sm:text-3xl text-[#1A1A1A]">
                    {p.name}
                  </h3>
                  <p className="mt-4 text-sm text-[#4A4540] leading-relaxed">{p.summary}</p>
                  <p className="mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-semibold text-[#2A4D3B]">
                    Discover {p.name} <ArrowUpRight className="h-3.5 w-3.5" />
                  </p>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* BROKERAGE - DARK BREAKOUT */}
      <section
        data-testid="brokerage-section"
        className="relative px-6 sm:px-12 lg:px-24 py-28 sm:py-36 bg-[#1A1A1A] text-[#FDFBF7] overflow-hidden"
      >
        <img
          src={IMAGES.network}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-[#1A1A1A]/80" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <FadeIn className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.24em] text-[#D8A48F] font-bold">
              International brokerage
            </p>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              The pulses brokerage of choice for traders who want trust, speed and clean execution.
            </h2>
            <p className="mt-7 max-w-2xl text-[#C8C2B5] leading-relaxed">
              Through Farmico Proteins FZCO, we broker pulses between origin shippers and global
              importers — built on personal relationships, transparent terms, and deep market read.
            </p>
            <Link
              to="/brokerage"
              data-testid="brokerage-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#1A1A1A] px-6 py-3.5 text-sm font-semibold hover:bg-[#D8A48F] transition-colors"
            >
              How brokerage works →
            </Link>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { icon: Globe2, label: "Origin Networks", desc: "India · Africa · Brazil · Canada · Australia · Russia" },
              { icon: Handshake, label: "Trusted Matching", desc: "Buyer ↔ shipper introductions" },
              { icon: BarChart3, label: "Market Intel", desc: "Daily price reads & flow updates" },
              { icon: Ship, label: "Clean Execution", desc: "End-to-end shipment oversight" },
            ].map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="rounded-2xl bg-[#FDFBF7]/5 backdrop-blur-md border border-white/10 p-6"
              >
                <Icon className="h-6 w-6 text-[#D8A48F]" />
                <p className="mt-5 font-semibold text-[#FDFBF7]">{label}</p>
                <p className="mt-2 text-xs text-[#C8C2B5] leading-relaxed">{desc}</p>
              </div>
            ))}
          </FadeIn>
        </div>
      </section>

      {/* ABOUT / EDITORIAL SPLIT */}
      <Section testid="about-split-section" className="bg-[#FDFBF7]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 relative">
            <FadeIn>
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border-4 border-[#FDFBF7] shadow-2xl">
                <img src={IMAGES.harvest} alt="Harvest" className="h-full w-full object-cover" />
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="hidden sm:block absolute -bottom-12 -right-6 w-2/3 aspect-[4/3] rounded-3xl overflow-hidden border-4 border-[#FDFBF7] shadow-2xl">
                <img src={IMAGES.shipping} alt="Shipping" className="h-full w-full object-cover" />
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-6">
            <Overline>Why Farmico</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.08] text-[#1A1A1A]">
              Grounded in the field. Built for the world.
            </h2>
            <p className="mt-7 text-[#4A4540] leading-relaxed">
              We started where the crop is grown — walking origins, building relationships with farmer
              cooperatives, processors and shippers. Today, our UAE trading desk turns that ground
              truth into reliable supply for buyers in various countries.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <ShieldCheck className="h-6 w-6 text-[#2A4D3B] shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-[#1A1A1A]">Spec-Bound Quality</p>
                  <p className="text-sm text-[#4A4540] mt-1">
                    Third-party inspections at origin and destination.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Ship className="h-6 w-6 text-[#2A4D3B] shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-[#1A1A1A]">Reliable Logistics</p>
                  <p className="text-sm text-[#4A4540] mt-1">
                    Pre-booked freight on key trade lanes, year-round.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Globe2 className="h-6 w-6 text-[#2A4D3B] shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-[#1A1A1A]">Multi-Origin Sourcing</p>
                  <p className="text-sm text-[#4A4540] mt-1">
                    Coverage across 6 continents and 20+ origins.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Handshake className="h-6 w-6 text-[#2A4D3B] shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-[#1A1A1A]">Relationship-Led</p>
                  <p className="text-sm text-[#4A4540] mt-1">
                    Long-standing partnerships, transparent dealings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* GLOBAL REACH STRIP */}
      <Section testid="reach-section" className="bg-[#F6F4F0]">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 items-start">
          <div>
            <Overline>Global presence</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1A1A]">
              From the UAE, to every major trading lane on Earth.
            </h2>
            <Link
              to="/global-presence"
              data-testid="reach-cta"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#2A4D3B] link-underline"
            >
              See the trade map →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {REGIONS.map((r, i) => (
              <FadeIn key={r.region} delay={i * 0.05}>
                <div className="rounded-2xl bg-white p-6 border border-[#EAE7E0] hover:border-[#2A4D3B]/40 transition-colors">
                  <p className="font-serif-display text-lg text-[#1A1A1A]">{r.region}</p>
                  <p className="mt-2 text-sm text-[#4A4540]">{r.note}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </Section>
    </main>
  );
}
